import { useEffect, useMemo, useRef } from 'react';

import { getCommonRandomizationUnits } from './getCommonRandomizationUnits';
import { useMetricsByKeysWithGroups } from './useMetricsByKeysWithGroups';

interface UseSetMetricsHaveCommonRandomizationUnitsProps {
  metricKeys?: string[];
  metricGroupKeys?: string[];
  setFieldValue: (value: boolean) => void;
  getFieldValue: () => boolean | undefined;
}

/**
 * Hook that automatically computes and sets whether all selected metrics
 * share a common randomization unit. This is used for validation to ensure
 * metrics can be used together in a guarded rollout or release policy.
 */
export function useSetMetricsHaveCommonRandomizationUnits({
  metricKeys,
  metricGroupKeys,
  setFieldValue,
  getFieldValue,
}: UseSetMetricsHaveCommonRandomizationUnitsProps) {
  const { metrics: allMetrics, isPending } = useMetricsByKeysWithGroups({ metricKeys, metricGroupKeys });

  // Use refs to avoid effect re-running when callback references change
  const setFieldValueRef = useRef(setFieldValue);
  setFieldValueRef.current = setFieldValue;
  const getFieldValueRef = useRef(getFieldValue);
  getFieldValueRef.current = getFieldValue;

  // Compute validation result, memoized by actual metric content
  const hasCommonUnits = useMemo(() => {
    if (isPending || allMetrics.length === 0) {
      return true;
    }
    return getCommonRandomizationUnits(allMetrics).length > 0;
  }, [allMetrics, isPending]);

  useEffect(() => {
    if (isPending) {
      return;
    }
    // Only update if value has changed
    if (getFieldValueRef.current() !== hasCommonUnits) {
      setFieldValueRef.current(hasCommonUnits);
    }
  }, [hasCommonUnits, isPending]);
}
